import {
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  Ref,
} from 'react';
import {
  TextField as MUITextField,
  TextFieldProps as MUITextFieldProps,
} from '@mui/material';
import * as R from 'ramda';

import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

// Props that do not have direct mapping to MUI
type CustomProps = {
  /**
   * style to be applied to the input container
   */
  style?: CSSProperties;
  /**
   * Fix label to "floating" position (focused)
   * defaults to true
   */
  floatingLabelFixed?: boolean;
  /**
   * Color for the fixed floating label
   * defaults to colors.neutral.gray
   */
  inputLabelColor?: string;
};

// All props currently used that have direct MUI mapping as of MUI v6.5.0
type AtomMUIProps = {
  slotProps?: {
    input?: {
      style?: CSSProperties;
      sx?: any;
      disableUnderline?: boolean;
      startAdornment?: ReactNode;
      endAdornment?: ReactNode;
      onMouseEnter?: () => void;
      onMouseLeave?: () => void;
      readOnly?: boolean;
      min?: number | string;
      max?: number | string;
    };
    inputLabel?: {
      style?: CSSProperties;
      sx?: any;
    };
    formHelperText?: {
      style?: CSSProperties;
      sx?: any;
    };
    htmlInput?: Record<string, any>;
  };
  autoComplete?: string;
  autoFocus?: boolean;
  defaultValue?: unknown;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: ReactNode;
  id?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'search'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal';
  inputRef?: Ref<any>;
  label?: ReactNode;
  margin?: 'normal' | 'none' | 'dense';
  maxRows?: string | number;
  multiline?: boolean;
  name?: string;
  onBlur?: FocusEventHandler<any>;
  onClick?: () => void;
  onChange?: ChangeEventHandler<any>;
  onFocus?: FocusEventHandler<any>;
  onKeyDown?: KeyboardEventHandler<any>;
  onKeyUp?: KeyboardEventHandler<any>;
  placeholder?: string;
  required?: boolean;
  step?: string;
  size?: 'small' | 'medium';
  type?: string;
  value?: unknown;
  variant?: 'filled' | 'outlined' | 'standard';
};

export type TextFieldProps = CustomProps & AtomMUIProps;

const getStyles = (props: TextFieldProps): { [key: string]: any } => {
  const { floatingLabelFixed = true, inputLabelColor = colors.neutral.gray } =
    props;

  const inputLabelBase = floatingLabelFixed
    ? { position: 'unset', color: inputLabelColor }
    : {
        color: inputLabelColor,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        bottom: '0px',
        maxWidth: '100%',
      };

  return {
    inputLabel: {
      '&.MuiInputLabel-standard': inputLabelBase,
      '&.Mui-focused': {
        color: `${colors.neutral.gray} !important`,
      },
      '&.Mui-error': {
        color: `${colors.utility.muiError} !important`,
      },
      ...(floatingLabelFixed && {
        '&.MuiInputLabel-shrink': { transform: 'unset', fontSize: fonts.sm },
      }),
    },
    input: {
      fontSize: fonts.md,
      'label + &': {
        marginTop: '0',
      },
      '&.MuiInput-underline': {
        '&:before': {
          borderBottom: `1px solid ${colors.neutral.silver}`,
        },
      },
    },
    formHelperText: {
      '&.MuiFormHelperText-root': {
        height: 0,
        marginTop: 0,
      },
    },
  };
};

const defaultContainerStyle: CSSProperties = {
  width: '100%',
};

export const TextField = (props: TextFieldProps) => {
  const {
    style,
    floatingLabelFixed = true,
    fullWidth = true,
    autoComplete = 'off',
    variant = 'standard',
    slotProps: callerSlotProps = {},
    ...data
  } = props;

  const styles = getStyles({ ...props, floatingLabelFixed });

  const MUIProps: MUITextFieldProps = R.omit(['inputLabelColor'], data);

  return (
    <div style={{ ...defaultContainerStyle, ...style }}>
      <MUITextField
        {...MUIProps}
        variant={variant}
        fullWidth={fullWidth}
        autoComplete={autoComplete}
        slotProps={{
          input: {
            ...callerSlotProps.input,
            sx: {
              ...styles.input,
              ...callerSlotProps.input?.sx,
            },
          },
          inputLabel: {
            ...callerSlotProps.inputLabel,
            sx: {
              ...styles.inputLabel,
              ...callerSlotProps.inputLabel?.sx,
            },
            ...(floatingLabelFixed && { shrink: true }),
          },
          formHelperText: {
            ...callerSlotProps.formHelperText,
            sx: {
              ...styles.formHelperText,
              ...callerSlotProps.formHelperText?.sx,
            },
          },
          htmlInput: callerSlotProps.htmlInput,
        }}
      />
    </div>
  );
};

export default TextField;
